/**
 * HelpCenterPage — `/service-desk/help-center`.
 *
 * Slice B10: admin CRUD for published help articles.
 * Wired to GET/POST/PUT/DELETE /v1/service-desk/help-articles.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Edit2, Eye, EyeOff, Plus, Search, Trash2, X } from 'lucide-react';

import {
  createHelpArticle,
  deleteHelpArticle,
  listHelpArticles,
  updateHelpArticle,
  type HelpArticle,
} from '../../lib/api/service-desk';
import { PageHeader } from '../shared/StaffUI';

interface ArticleFormState {
  title: string;
  slug: string;
  body: string;
  category: string;
  published: boolean;
}

const EMPTY: ArticleFormState = { title: '', slug: '', body: '', category: 'General', published: false };

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function HelpCenterPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ArticleFormState>(EMPTY);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['service-desk', 'help-articles'],
    queryFn: () => listHelpArticles(),
  });

  const createMut = useMutation({
    mutationFn: (b: ArticleFormState) =>
      createHelpArticle({ title: b.title, slug: b.slug, body: b.body, category: b.category, published: b.published }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-desk', 'help-articles'] });
      closeForm();
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, ...b }: ArticleFormState & { id: string }) =>
      updateHelpArticle(id, { title: b.title, body: b.body, category: b.category, published: b.published }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-desk', 'help-articles'] });
      closeForm();
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteHelpArticle(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-desk', 'help-articles'] });
    },
  });

  const togglePublishMut = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      updateHelpArticle(id, { published }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-desk', 'help-articles'] });
    },
  });

  function openNew(): void {
    setEditingId(null);
    setForm(EMPTY);
    setShowForm(true);
  }

  function openEdit(a: HelpArticle): void {
    setEditingId(a.id);
    setForm({ title: a.title, slug: a.slug, body: a.body, category: a.category, published: a.published });
    setShowForm(true);
  }

  function closeForm(): void {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY);
  }

  function handleTitleChange(val: string): void {
    setForm((f) => ({ ...f, title: val, slug: editingId ? f.slug : slugify(val) }));
  }

  function handleSubmit(): void {
    if (editingId) {
      updateMut.mutate({ id: editingId, ...form });
    } else {
      createMut.mutate(form);
    }
  }

  const isSaving = createMut.isPending || updateMut.isPending;
  const saveError = createMut.error ?? updateMut.error;

  const items = data?.items ?? [];
  const filtered = search.trim()
    ? items.filter(
        (a) =>
          a.title.toLowerCase().includes(search.toLowerCase()) ||
          a.category.toLowerCase().includes(search.toLowerCase()),
      )
    : items;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Help centre"
        description="Published help articles — admin CRUD."
        actions={
          <button
            type="button"
            onClick={openNew}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New article
          </button>
        }
      />

      {isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Failed to load articles: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      ) : null}

      {/* Article form */}
      {showForm ? (
        <div className="bg-white border border-nipost-green/30 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">{editingId ? 'Edit article' : 'New article'}</p>
            <button type="button" onClick={closeForm} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-4 h-4 text-muted" />
            </button>
          </div>

          {saveError ? (
            <div className="rounded bg-red-50 border border-red-200 p-2 text-xs text-red-900">
              {saveError instanceof Error ? saveError.message : 'Save failed'}
            </div>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                placeholder="Article title"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Slug</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
                placeholder="article-slug"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Category</label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input
                type="checkbox"
                id="published"
                checked={form.published}
                onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                className="w-4 h-4"
              />
              <label htmlFor="published" className="text-sm text-gray-700">Publish immediately</label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted mb-1">Body</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              rows={6}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              placeholder="Article content (markdown or plain text)"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving || !form.title.trim() || !form.slug.trim() || !form.body.trim()}
              className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-60"
            >
              {isSaving ? 'Saving...' : editingId ? 'Update' : 'Publish'}
            </button>
            <button type="button" onClick={closeForm} className="px-4 py-2 border border-border rounded-lg text-sm">
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search articles..."
          className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm"
        />
      </div>

      {/* Articles list */}
      <div className="bg-white border border-border rounded-xl divide-y divide-border">
        <div className="px-4 py-3 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-nipost-green" />
          <p className="text-sm font-semibold text-gray-900">
            Help articles ({isPending ? '...' : data?.total ?? 0})
          </p>
        </div>

        {isPending ? (
          <p className="px-4 py-6 text-center text-sm text-muted">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted">No articles found. Click &quot;New article&quot; to create one.</p>
        ) : (
          filtered.map((a) => (
            <div key={a.id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate">{a.title}</p>
                  {a.published ? (
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 rounded">Published</span>
                  ) : (
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-gray-100 text-gray-500 rounded">Draft</span>
                  )}
                </div>
                <p className="text-xs text-muted mt-0.5">{a.category} &middot; {a.slug}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => togglePublishMut.mutate({ id: a.id, published: !a.published })}
                  className="p-1.5 rounded hover:bg-gray-100 text-muted"
                  aria-label={a.published ? 'Unpublish' : 'Publish'}
                  title={a.published ? 'Unpublish' : 'Publish'}
                >
                  {a.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => openEdit(a)}
                  className="p-1.5 rounded hover:bg-gray-100 text-muted hover:text-nipost-green"
                  aria-label={`Edit ${a.title}`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => deleteMut.mutate(a.id)}
                  className="p-1.5 rounded hover:bg-gray-100 text-muted hover:text-red-600"
                  aria-label={`Delete ${a.title}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
